import { IAccordionItemData, IAccordionWidgetData, IAccordionWidgetStyle } from "@pageBuilder/typings";
import { AccordionWidgetScriptRenderer } from "./accordion-widget-script.renderer";
import { CssProperties } from "../../../models/css-property";
import { WidgetRendererBase } from "../widget-renderer-base";
import { IRenderingCommonContext } from "@commonRendering/types";

export class AccordionWidgetRenderer extends WidgetRendererBase<IAccordionWidgetData> {

    private readonly scriptRenderer: AccordionWidgetScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new AccordionWidgetScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("accordionWidget");
    }

    protected renderInternal(element: HTMLElement, data: IAccordionWidgetData): HTMLElement {
        const itemGroup = document.createElement("div");
        element.classList.toggle("expand-first", data.expandFirst);
        element.classList.toggle("allow-multiple", data.allowMultiple);
        data.items.forEach(item => {
            const itemContainer = this.renderItem(item, data.style);
            itemGroup.appendChild(itemContainer);
        });

        const content = element.querySelector(".widget-content");
        if (content?.hasChildNodes()) {
            content.removeChild(content.firstChild as Node);
        }
        content?.appendChild(itemGroup);
        this.renderStyles(element, data);
        if (data.expandFirst) {
            this.renderFirst(element);
        }
        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        return element;
    }

    private renderItem(item: IAccordionItemData, style?: IAccordionWidgetStyle | null): HTMLDivElement {
        const itemContainer = document.createElement("div");
        itemContainer.classList.add("item-container");
        itemContainer.setAttribute("item-id", `${item.id}`);

        const icon = document.createElement("span");
        if (style?.design?.iconStyle == "plus") {
            icon.classList.add("plus");
        } else {
            icon.classList.add("arrow");
        }
        icon.classList.add("item-icon");
        icon.classList.add("closed");

        const titleIconContainer = document.createElement("div");
        titleIconContainer.classList.add("title-icon-wrapper");

        const title = document.createElement("h2");
        title.classList.add("title");
        title.textContent = item? item.title : "";

        const description = document.createElement("p");
        description.classList.add("description");
        description.classList.add("hidden");
        description.classList.add("visually-hidden");
        description.textContent = item? item.description : "";

        titleIconContainer.appendChild(title);
        titleIconContainer.appendChild(icon);

        itemContainer.appendChild(titleIconContainer);
        itemContainer.appendChild(description);
        return itemContainer;
    }

    private renderFirst(element: HTMLElement): void {
        const item = element.getElementsByClassName("item-container")[0] as HTMLDivElement;
        const description = item.querySelector("p");
        const icon = item.querySelector(".item-icon");
        description?.classList.toggle("hidden");
        description?.classList.toggle("visually-hidden");
        icon?.classList.toggle("closed");
    }

    protected renderStyles(element: HTMLElement, data: IAccordionWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderIconStyles(element, data.style);
        this.renderTitleStyles(element, data.style);
        this.renderDescriptionStyles(element, data.style);
        this.renderDividerStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style?: IAccordionWidgetStyle | null): void {
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private renderIconStyles(element: HTMLElement, style?: IAccordionWidgetStyle | null): void {
        const iconProps = new CssProperties();
        iconProps.tryAdd("--icon-size", `20px`);
        iconProps.tryAdd("--icon-color", style?.design?.iconColor || "#7B828E");

        this.styleService.setStyle(element, `#${element.id} .item-icon`, iconProps.data);
    }

    private renderTitleStyles(element: HTMLElement, style?: IAccordionWidgetStyle | null): void {
        const titleProps = new CssProperties();

        titleProps.tryAdd("--font-family", style?.title?.font?.family ?? "");
        titleProps.tryAdd("--color", style?.title?.font?.color ?? "");
        titleProps.tryAdd("--font-weight", style?.title?.font?.weight ?? "");

        this.styleService.setStyle(element, `#${element.id} .title`, titleProps.data);
    }

    private renderDescriptionStyles(element: HTMLElement, style?: IAccordionWidgetStyle | null): void {
        const descriptionProps = new CssProperties();
        descriptionProps.tryAdd("--font-family", style?.description?.font?.family ?? "");
        descriptionProps.tryAdd("--color", style?.description?.font?.color ?? "");
        descriptionProps.tryAdd("--font-weight", style?.description?.font?.weight ?? "");

        this.styleService.setStyle(element, `#${element.id} .description`, descriptionProps.data);
    }

    private renderDividerStyles(element: HTMLElement, style?: IAccordionWidgetStyle | null): void {
        const dividerProps = new CssProperties();
        dividerProps.tryAdd("border-bottom-width", `${style?.design?.dividerThickness}px` || "1px");
        dividerProps.tryAdd("border-bottom-style", `${style?.design?.dividerStyle}` || "solid");
        dividerProps.tryAdd("border-bottom-color", `${style?.design?.dividerColor}` || "#DBDBDB");

        this.styleService.setStyle(element, `#${element.id} .item-container`, dividerProps.data);
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .title`, `#${element.id} .description`, `#${element.id} .item-container`, `#${element.id} .item-icon`];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

}
